import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Base styles - every variant gets these
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "rounded-md text-sm font-medium font-sans",
    "transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        // Primary action - use for the single most important action on a page
        default:
          "bg-primary text-primary-foreground hover:bg-primary-hover",

        // Outline - secondary action, same weight as primary visually
        outline:
          "border border-primary bg-transparent text-primary hover:bg-primary-subtle",

        // Ghost - tertiary / icon buttons, low visual weight
        ghost:
          "text-primary hover:bg-primary-subtle",

        // Secondary - neutral alternative, use alongside a primary button
        secondary:
          "bg-muted text-foreground hover:bg-muted/70",

        // Destructive - irreversible actions (delete, remove)
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",

        // Info - informational / secondary actions (show/hide, save defaults)
        info:
          "bg-[hsl(var(--info))] text-white border-[hsl(var(--info))] hover:opacity-90",

        // Link - inline text links that behave like buttons
        link:
          "text-primary underline-offset-4 hover:underline p-0 h-auto",
      },
      size: {
        sm:      "h-8 px-3 text-xs rounded-md",
        default: "h-10 px-4 py-2",
        lg:      "h-11 px-8 rounded-md",
        icon:    "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Spinner = () => (
  <svg
    className="animate-spin h-4 w-4"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const Button = React.forwardRef((
  { className, variant, size, asChild = false, loading = false, children, ...props },
  ref
) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={loading || props.disabled}
      {...props}
    >
      {!asChild && loading && <Spinner />}
      {children}
    </Comp>
  );
});
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
